/**
 * Kordaro — platformmerk (huisstijl v1.0, 1/11/2026).
 * Puur (geen DB/DOM) zodat server (e-mail, PDF, brand-context) én client dezelfde regel gebruiken.
 *
 * Winkels zonder eigen huisstijl krijgen Kordaro. Het oude C-Bridge-goud (#C8A961 / #2C2C2C)
 * staat als schema-default in WinkelBranding en betekent dus "niet ingesteld": ook dat wordt Kordaro.
 * Een bewust gekozen winkelkleur (Heytens, Ixina, …) blijft altijd staan.
 */

export const PLATFORM_NAAM = 'Kordaro';

export const KORDARO = {
  bordeaux: '#592F38',
  bordeauxHover: '#47252D',
  ivory: '#F7F2EC',
  champagne: '#B0926D',
  espresso: '#352B26',
  surface: '#FFFFFF',
  border: '#E6DDD4',
  selected: '#F0E5E7',
  muted: '#74665F',
} as const;

/** Primair = merk/actie, secundair = donkere kopbalken (headers, PDF-band). */
export const DEFAULT_PRIMAIR = KORDARO.bordeaux;
export const DEFAULT_SECUNDAIR = KORDARO.espresso;

/** Schema-defaults van WinkelBranding uit de C-Bridge-tijd — betekenen "niet ingesteld". */
export const LEGACY_PRIMAIR = '#C8A961';
export const LEGACY_SECUNDAIR = '#2C2C2C';

/** Logo-paden (public/brand/kordaro). */
export const KORDARO_LOGO = {
  primary: '/brand/kordaro/kordaro-logo-primary.svg',
  bordeaux: '/brand/kordaro/kordaro-logo-bordeaux.svg',
  ivory: '/brand/kordaro/kordaro-logo-ivory.svg',
  ivoryPng: '/brand/kordaro/kordaro-logo-ivory.png',
  markPrimary: '/brand/kordaro/kordaro-mark-primary.svg',
  markIvory: '/brand/kordaro/kordaro-mark-ivory.svg',
} as const;

const HEX6 = /^#[0-9a-f]{6}$/i;
const HEX3 = /^#[0-9a-f]{3}$/i;

function toHex6(v: string | null | undefined): string | null {
  const t = (v ?? '').trim();
  if (HEX6.test(t)) return t.toUpperCase();
  if (HEX3.test(t)) return ('#' + t[1] + t[1] + t[2] + t[2] + t[3] + t[3]).toUpperCase();
  return null;
}

/** Ingestelde primaire kleur, of Kordaro als niets (of het oude schema-goud) is ingesteld. */
export function resolvePrimair(v: string | null | undefined): string {
  const h = toHex6(v);
  return !h || h === LEGACY_PRIMAIR ? DEFAULT_PRIMAIR : h;
}

/** Ingestelde secundaire kleur, of Kordaro als niets (of het oude schema-antraciet) is ingesteld. */
export function resolveSecundair(v: string | null | undefined): string {
  const h = toHex6(v);
  return !h || h === LEGACY_SECUNDAIR ? DEFAULT_SECUNDAIR : h;
}

/** true = deze kleur is niet bewust door de winkel gekozen. */
export function isNietIngesteld(primair: string | null | undefined): boolean {
  const h = toHex6(primair);
  return !h || h === LEGACY_PRIMAIR;
}

/** WCAG-contrastverhouding tussen twee hex-kleuren. */
export function contrastHex(a: string, b: string): number {
  const lum = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    const f = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
    return 0.2126 * f((n >> 16) & 255) + 0.7152 * f((n >> 8) & 255) + 0.0722 * f(n & 255);
  };
  const [l1, l2] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** Tekstkleur voor een accent op een donkere balk: primair als die leesbaar is, anders ivoor. */
export function accentOpDonker(primair: string, donker: string): string {
  return contrastHex(primair, donker) >= 4.5 ? primair : KORDARO.ivory;
}
